import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IconButton, Paper, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import { Trash01 } from '../../../assets';
import useLogError from '../../../hooks/useLogError';
import { Notification2 } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import CreateUpdateNotificationModal from './NotificationEditCreateModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { formatDateTime } from '../../../utils';

interface RolesTableProps {
  notifications: Notification2[];
  loadData: () => void;
}

const NotificationsTable = ({ notifications, loadData }: RolesTableProps) => {
  const { t } = useTranslation('manageNotifications');
  const theme = useTheme();
  const { saveNotification, deleteNotification } = useNswagClient();
  const [data, setData] = React.useState(notifications);
  const [selectedNotification, setSelectedNotification] = React.useState<Notification2 | undefined>(undefined);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDeleteSuccessMessage, setshowDeleteSuccessMessage] = React.useState(false);
  const [showUpdateSuccessMessage, setShowUpdateSuccessMessage] = React.useState(false);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Notification2>('title' as keyof Notification2);
  const { logError } = useLogError();

  React.useEffect(() => {
    setData(notifications);
  }, [notifications]);

  const handleSort = (property: keyof Notification2) => {
    const isAsc = orderBy === property && order === 'asc';
    if (data) {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      handleSearchChange();
    }
  };

  const sortedData = () => {
    const isAsc = order === 'asc';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const comparator = (a: any, b: any) => {

      if (typeof a[orderBy] === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fistString = a[orderBy].replace(/<\/?[^>]+(>|$)/g, '');
        const secondString = b[orderBy].replace(/<\/?[^>]+(>|$)/g, '');
        return (isAsc ? fistString.localeCompare(secondString) : secondString.localeCompare(fistString));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]) as any;
      }
    };
    return notifications.sort(comparator);
  };

  const editNotification = (notification: Notification2 | undefined) => {
    setSelectedNotification(notification);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedNotification(undefined);
  };

  const deleteNotif = (notification: Notification2) => {
    setSelectedNotification(notification);
    setShowDeleteModal(true);
  };

  const handleSearchChange = () => {
    setData(sortedData());
  };

  const handleEdit = (body: Notification) => {
    saveNotification(body)
      .then(() => {
        setShowUpdateSuccessMessage(true);
        setTimeout(() => loadData(), 2000);
      })
      .catch((error) => logError(error));
    setSelectedNotification(undefined);
  };

  const handleDelete = () => {
    deleteNotification(selectedNotification?.notificationId ?? 0)
      .then(() => {
        setshowDeleteSuccessMessage(true);
        setTimeout(() => loadData(), 2000);
      })
      .catch((error) => logError(error));

    setShowDeleteModal(false);
    setSelectedNotification(undefined);
  };

  const closeSuccessfulDeleteMessage = () => {
    setshowDeleteSuccessMessage(false);
  };

  const addMaxSizeToImages = (htmlString: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const images = Array.from(tempDiv.getElementsByTagName('img'));
    images.forEach(img => {
      img.style.maxWidth = '400px';
    });
    return tempDiv.innerHTML;
  };

  return (
    <>
      <TableContainer sx={{ borderRadius: '12px' }}
        component={Paper}>
        <Table
          size="small"
          aria-label="a table"
        >
          <TableHead sx={{ paddingTop: '10px', backgroundColor: theme.palette.custom.gray[200] }}>
            <TableRow>
              <TableCell>
                ID
              </TableCell>
              <TableCell
                align="left"
                width={'25%'}>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={order}
                  onClick={() => handleSort('title')}>
                  {t('title')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left"
                width={'40%'}>
                <TableSortLabel
                  active={orderBy === 'message'}
                  direction={order}
                  onClick={() => handleSort('message')}>
                  {t('body')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left">
                <TableSortLabel
                  active={orderBy === 'startDate'}
                  direction={order}
                  onClick={() => handleSort('startDate')}>
                  {t('startDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left">
                <TableSortLabel
                  active={orderBy === 'endDate'}
                  direction={order}
                  onClick={() => handleSort('endDate')}>
                  {t('endDate')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={index}
                sx={{ height: '60px', cursor: 'pointer' }}
                onClick={() => editNotification(row)}
              >
                <TableCell>
                  <Typography variant='textXS'
                    fontWeight={600}
                    color={theme.palette.primary[500]}
                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {index + 1}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.title}
                </TableCell>
                <TableCell>
                  <div 
                    /* skipcq: JS-0440 Notification body HTML is rendered after ensuring limits on embedded images. */
                    dangerouslySetInnerHTML={{ __html: addMaxSizeToImages(row.message as string) }}
                  />
                </TableCell>
                <TableCell
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'startDate'}
                    direction={order}
                    onClick={() => handleSort('startDate')}>
                    {formatDateTime(row.startDate ?? '')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'endDate'}
                    direction={order}
                    onClick={() => handleSort('endDate')}>
                    {formatDateTime(row.endDate ?? '')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <IconButton aria-label={t('deleteModal.confirm')}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotif(row);
                    }}>
                    <SvgIcon fontSize='small'><Trash01 color={theme.palette.error[500]} /></SvgIcon>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateUpdateNotificationModal
        openModal={showEditModal}
        onCancel={handleCloseModal}
        notification={selectedNotification}
        onConfirm={(requestBody: Notification) => handleEdit(requestBody)}
      />

      <ConfirmationDialog open={showDeleteModal}
        message={t('deleteModal.confirmMessage')}
        onConfirm={handleDelete}
        onCancel={handleCloseModal}
        icon={<Trash01 />}
        title={t('deleteModal.confirm')}
        variant='bad'
        confirmButtonText='Delete'
        cancelButtonText='Cancel'
      />

      <MessagesSnackbar
        open={showDeleteSuccessMessage}
        onClose={closeSuccessfulDeleteMessage}
        message='Notification deleted'
        severity="success"
        duration={null}
      />

      <MessagesSnackbar
        open={showUpdateSuccessMessage}
        onClose={() => setShowUpdateSuccessMessage(false)}
        message={'Notification updated successfully'}
        severity="success"
        duration={null}
      />
    </>
  );
};

export default NotificationsTable;
